<template>
  <SidebarComponent />
  <div class="dashboard-layout">
    <div class="content-container">
      <div class="card-container">
        <div class="titolo-card">
          <h1>{{ resolvedPageTitle }}</h1>
          <h3>{{ resolvedPageSubtitle }}</h3>
        </div>

        <section class="toolbar-card card-surface">
          <div class="filters-header">
            <button
              type="button"
              class="accordion-toggle"
              @click="filtersOpen = !filtersOpen"
              :aria-expanded="filtersOpen"
            >
              <div class="toggle-copy">
                <span class="toggle-title">Filtri e azioni</span>
                <span class="toggle-subtitle">Intervallo, stato, backfill</span>
              </div>
              <span class="material-icons" aria-hidden="true">
                {{ filtersOpen ? 'expand_less' : 'expand_more' }}
              </span>
            </button>
          </div>

          <transition name="accordion">
            <div v-show="filtersOpen" class="accordion-panel">
              <div class="toolbar">
                <div class="toolbar-group toolbar-group--sm">
                  <label class="toolbar-label" for="notif-from">Dal</label>
                  <input
                    id="notif-from"
                    type="date"
                    v-model="filterFrom"
                    class="input date-range-input date-range-input--short"
                  />
                </div>
                <div class="toolbar-group toolbar-group--sm">
                  <label class="toolbar-label" for="notif-to">Al</label>
                  <input
                    id="notif-to"
                    type="date"
                    v-model="filterTo"
                    class="input date-range-input date-range-input--short"
                  />
                </div>
                <div class="toolbar-group toolbar-group--md">
                  <label class="toolbar-label" for="notif-status">Stato</label>
                  <select id="notif-status" v-model="statusFilter" class="input">
                    <option value="">Tutti</option>
                    <option value="upcoming">In arrivo</option>
                    <option value="today">Oggi</option>
                    <option value="overdue">In ritardo</option>
                    <option value="sent">Inviata</option>
                    <option value="blocked">Bloccato</option>
                    <option value="disabled">Notifiche off</option>
                  </select>
                </div>
                <div class="toolbar-group toolbar-group--md">
                  <label class="toolbar-label" for="notif-studio">Studio</label>
                  <select id="notif-studio" v-model="studioFilter" class="input">
                    <option value="">Tutti</option>
                    <option :value="noStudioFilterValue">NO STUDIO</option>
                    <option v-for="studio in studioOptions" :key="studio" :value="studio">{{ studio }}</option>
                  </select>
                </div>

                <div class="toolbar-actions">
                  <button class="btn primary staff-button-neutral" type="button" @click="resetToDefaultRange">
                    Ieri/Oggi
                  </button>
                </div>

                <template v-if="canUseReminderTools">
                  <div class="toolbar-group toolbar-group--lg">
                    <label class="toolbar-label" for="backfill-percorso">Percorso ID</label>
                    <input
                      id="backfill-percorso"
                      type="text"
                      v-model.trim="backfillPercorsoId"
                      class="input input--compact"
                      placeholder="(opz.)"
                    />
                  </div>
                  <div class="toolbar-group toolbar-group--lg">
                    <label class="toolbar-label" for="diagnostic-user">Email/UID</label>
                    <input
                      id="diagnostic-user"
                      type="text"
                      v-model.trim="diagnosticUserKey"
                      class="input input--compact"
                      placeholder="email o uid"
                    />
                  </div>
                  <div class="toolbar-group toolbar-group--sm">
                    <label class="toolbar-label" for="backfill-limit">Limite percorsi</label>
                    <input
                      id="backfill-limit"
                      type="number"
                      min="1"
                      v-model.number="backfillLimit"
                      class="input input--compact"
                      placeholder="(opz.)"
                    />
                  </div>
                  <div class="toolbar-actions">
                    <button
                      v-if="canRunBackfill"
                      class="btn ghost"
                      type="button"
                      :disabled="backfillRunning || rangeBackfillRunning"
                      @click="runBackfill"
                    >
                      {{ backfillRunning ? 'Backfill…' : 'Aggiorna indice' }}
                    </button>
                    <button
                      class="btn ghost"
                      type="button"
                      :disabled="diagnosticRunning || !backfillPercorsoId || backfillRunning || rangeBackfillRunning"
                      @click="runDiagnostic"
                    >
                      {{ diagnosticRunning ? 'Diagnostica…' : 'Diagnostica percorso' }}
                    </button>
                    <button
                      class="btn ghost"
                      type="button"
                      :disabled="diagnosticUserRunning || !diagnosticUserKey || backfillRunning || rangeBackfillRunning"
                      @click="runUserDiagnostic"
                    >
                      {{ diagnosticUserRunning ? 'Diagnostica…' : 'Diagnostica utente' }}
                    </button>
                    <button
                      v-if="canRunBackfill"
                      class="btn ghost staff-button-positive"
                      type="button"
                      :disabled="rangeBackfillRunning || !filterFrom || !filterTo || backfillRunning"
                      @click="runRangeBackfill"
                    >
                      {{ rangeBackfillRunning ? 'Analisi…' : 'Trova e salva (range)' }}
                    </button>
                  </div>
                </template>
              </div>

              <div
                v-if="backfillStatus || backfillError || rangeBackfillStatus || rangeBackfillError"
                class="backfill-feedback"
              >
                <span v-if="backfillStatus" class="muted">{{ backfillStatus }}</span>
                <span v-if="backfillError" class="error">{{ backfillError }}</span>
                <span v-if="rangeBackfillStatus" class="muted">{{ rangeBackfillStatus }}</span>
                <span v-if="rangeBackfillError" class="error">{{ rangeBackfillError }}</span>
              </div>
              <div v-if="diagnosticStatus || diagnosticError" class="backfill-feedback">
                <span v-if="diagnosticStatus" class="muted">{{ diagnosticStatus }}</span>
                <span v-if="diagnosticError" class="error">{{ diagnosticError }}</span>
              </div>
              <div v-if="diagnosticUserStatus || diagnosticUserError" class="backfill-feedback">
                <span v-if="diagnosticUserStatus" class="muted">{{ diagnosticUserStatus }}</span>
                <span v-if="diagnosticUserError" class="error">{{ diagnosticUserError }}</span>
              </div>
            </div>
          </transition>
        </section>

        <div v-if="loading" class="loader">
          <h4>Caricamento in corso…</h4>
        </div>

        <div v-else class="dataset-summary">
          <span>Mostrando {{ filteredRows.length }} notifiche</span>
        </div>

        <div v-if="rangePreviewRows.length" class="dataset-summary">
          <span>
            Previsti da percorsi (range): {{ rangePreviewRows.length }}
            <template v-if="rangePreviewLimit">/ max {{ rangePreviewLimit }}</template>
          </span>
        </div>

        <div v-if="rangePreviewRows.length" class="tickets">
          <table class="users-table">
            <thead>
              <tr>
                <th>Data invio</th>
                <th>Stato</th>
                <th>Utente</th>
                <th>Email</th>
                <th>Programma</th>
                <th>Percorso</th>
                <th>Step</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rangePreviewRows" :key="row.id">
                <td>{{ formatDateOnly(row.dueAt) || row.dueDateKey || '—' }}</td>
                <td>
                  <span class="status-pill" :class="statusClass(row)">
                    {{ statusLabel(row) }}
                  </span>
                </td>
                <td class="mono">{{ row.uid || '—' }}</td>
                <td class="mono">{{ row.userEmail || '—' }}</td>
                <td>{{ row.programTitle || 'Programma' }}</td>
                <td class="mono">{{ row.percorsoId || '—' }}</td>
                <td class="mono">{{ row.stepIndex ?? '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="diagnosticResult" class="dataset-summary">
          <span>
            Diagnostica {{ diagnosticResult.percorsoId }} —
            <template v-if="diagnosticResult.exists">trovato</template>
            <template v-else>non trovato</template>
          </span>
          <span v-if="diagnosticResult.exists">
            Consegnati: {{ diagnosticResult.consegnaStepsCount || 0 }},
            eleggibili: {{ diagnosticResult.eligibleCount || 0 }},
            reminder presenti: {{ diagnosticResult.reminderCount || 0 }}
          </span>
        </div>

        <div v-if="diagnosticRows.length" class="tickets">
          <table class="users-table">
            <thead>
              <tr>
                <th>Step</th>
                <th>Done</th>
                <th>DoneAt</th>
                <th>Due</th>
                <th>Doc atteso</th>
                <th>Esiste</th>
                <th>Motivo</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in diagnosticRows" :key="row.expectedDocId">
                <td class="mono">#{{ row.idx }} {{ row.name }}</td>
                <td>{{ row.done ? 'si' : 'no' }}</td>
                <td class="mono">{{ row.doneAtIso || row.doneAtRaw || '—' }}</td>
                <td class="mono">{{ row.dueDateKey || '—' }}</td>
                <td class="mono">{{ row.expectedDocId }}</td>
                <td>{{ row.reminderExists ? 'si' : 'no' }}</td>
                <td class="mono">{{ row.reasons?.length ? row.reasons.join(', ') : 'ok' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="diagnosticUserRows.length" class="dataset-summary">
          <span>
            Diagnostica utente — percorsi: {{ diagnosticUserSummary.found || 0 }}
            <template v-if="diagnosticUserSummary.maxPercorsi">
              (max {{ diagnosticUserSummary.maxPercorsi }})
            </template>
          </span>
        </div>

        <div v-if="diagnosticUserRows.length" class="tickets">
          <table class="users-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Percorso</th>
                <th>Step</th>
                <th>DoneAt</th>
                <th>Due</th>
                <th>Doc atteso</th>
                <th>Esiste</th>
                <th>Motivo</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in diagnosticUserRows" :key="row.expectedDocId + '-' + row.percorsoId">
                <td class="mono">{{ row.userEmail || '—' }}</td>
                <td class="mono">{{ row.percorsoId }}</td>
                <td class="mono">#{{ row.idx }} {{ row.name }}</td>
                <td class="mono">{{ row.doneAtIso || row.doneAtRaw || '—' }}</td>
                <td class="mono">{{ row.dueDateKey || '—' }}</td>
                <td class="mono">{{ row.expectedDocId }}</td>
                <td>{{ row.reminderExists ? 'si' : 'no' }}</td>
                <td class="mono">{{ row.reasons?.length ? row.reasons.join(', ') : 'ok' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="filteredRows.length" class="tickets">
          <table class="users-table">
            <thead>
              <tr>
                <th>Data invio</th>
                <th>Timing</th>
                <th>Stato</th>
                <th>Utente</th>
                <th>Email</th>
                <th>Programma</th>
                <th>Controllato</th>
                <th>Note</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in filteredRows" :key="row.key">
                <td>{{ formatDateOnly(row.dueAt) || '—' }}</td>
                <td class="muted">{{ timingLabel(row) }}</td>
                <td>
                  <span class="status-pill" :class="statusClass(row)">
                    {{ statusLabel(row) }}
                  </span>
                </td>
                <td>{{ row.userLabel }}</td>
                <td class="mono">{{ row.userEmail || '—' }}</td>
                <td>
                  <div class="mono" style="font-weight: 600;">{{ row.programTitle || 'Programma' }}</div>
                  <small v-if="row.sequenceLabel" class="muted">#{{ row.sequenceLabel }}</small>
                </td>
                <td>
                  <label class="checkbox-inline">
                    <input
                      type="checkbox"
                      v-model="row.controllato"
                      @change="saveControllato(row)"
                    />
                  </label>
                </td>
                <td>
                  <input
                    type="text"
                    class="input input--compact note-input"
                    v-model="row.note"
                    placeholder="Note…"
                    @input="queueNoteSave(row)"
                    @blur="queueNoteSave(row, true)"
                  />
                </td>
                <td>
                  <div class="buttons-container">
                    <button
                      type="button"
                      @click="openUser(row.uid)"
                      title="Apri programmi utente"
                      aria-label="Apri programmi utente"
                    >
                      <span class="material-icons">visibility</span>
                    </button>
                    <button
                      type="button"
                      @click="openTechnicalTicket(row)"
                      title="Apri ticket tecnico"
                      aria-label="Apri ticket tecnico"
                    >
                      <span class="material-icons">fitness_center</span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else-if="!loading" class="empty-state muted">
          Nessuna notifica nel periodo selezionato.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  query,
  orderBy,
  where,
  documentId,
  updateDoc
} from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import SidebarComponent from '../SidebarComponent.vue';

const DAY_MS = 24 * 60 * 60 * 1000;
const DEFAULT_FOLLOW_UP_DAYS = 10;
const USER_CHUNK_SIZE = 10;
const NO_STUDIO_FILTER = '__NO_STUDIO__';
const BACKFILL_PRIV_UID = 'HpMiFyBZnwX6EUf6VR9iCsHkqo82';
const BACKFILL_PRIV_EMAIL = 'valeriobianconi@gmail.com';
const STUDIO_OPTIONS = [
  'Tivoli Quartier generale',
  'FF Genova',
  'FF Bologna',
  'FF Bari',
  'FF Pescara',
  'FF Firenze',
  'FF Torino',
  'FF Napoli',
  'FF Milano',
  'FF Roma',
  'FF Salerno',
  'FF Cagliari',
  'FF Milano Città Studi',
  'FF Roma Eur',
  'FF Milano Sempione',
  'FF Padova'
];

const chunkList = (items, size) => {
  const out = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
};

const toDateSafe = (value) => {
  if (value === undefined || value === null) return null;
  if (typeof value?.toDate === 'function') {
    try {
      const converted = value.toDate();
      return converted instanceof Date && !Number.isNaN(converted.getTime()) ? converted : null;
    } catch {
      return null;
    }
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const toDateKey = (date) => {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const buildDaysDiff = (dueMs, nowMs) => {
  if (!Number.isFinite(dueMs) || !Number.isFinite(nowMs)) return null;
  if (dueMs >= nowMs) return Math.ceil((dueMs - nowMs) / DAY_MS);
  return -Math.ceil((nowMs - dueMs) / DAY_MS);
};

const getQueryValue = (value) => {
  if (Array.isArray(value)) return value[0] || '';
  return (value || '').toString();
};

const normalizeEmail = (value) => (value || '').toString().trim().toLowerCase();

const isValidDateKey = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return false;
  const date = new Date(`${value}T00:00:00`);
  return !Number.isNaN(date.getTime());
};

export default {
  name: 'Notifiche10Giorni',
  props: {
    followUpDays: {
      type: Number,
      default: DEFAULT_FOLLOW_UP_DAYS,
    },
    pageTitle: {
      type: String,
      default: '',
    },
    pageSubtitle: {
      type: String,
      default: '',
    },
  },
  components: { SidebarComponent },
  setup(props) {
    const db = getFirestore();
    const auth = getAuth();
    const router = useRouter();
    const route = useRoute();
    const resolvedFollowUpDays = computed(() => {
      const parsed = Number(props.followUpDays);
      return Number.isFinite(parsed) ? parsed : DEFAULT_FOLLOW_UP_DAYS;
    });
    const resolvedPageTitle = computed(() =>
      (props.pageTitle || '').trim() || `Notifiche ${resolvedFollowUpDays.value} giorni`
    );
    const resolvedPageSubtitle = computed(() =>
      (props.pageSubtitle || '').trim() || 'Promemoria generati dalla consegna programmi.'
    );

    const loading = ref(true);
    const rows = ref([]);
    const filterFrom = ref('');
    const filterTo = ref('');
    const statusFilter = ref('');
    const studioFilter = ref('');
    const filtersOpen = ref(true);
    const booted = ref(false);
    const userCache = new Map();
    const userRole = ref(localStorage.getItem('userRole') || '');
    const authUser = ref(auth.currentUser || null);
    const canUseReminderTools = computed(() => ['Amministratore', 'Studio'].includes(userRole.value));
    const canRunBackfill = computed(() => {
      const user = authUser.value || {};
      return user.uid === BACKFILL_PRIV_UID && normalizeEmail(user.email) === BACKFILL_PRIV_EMAIL;
    });
    const backfillLimit = ref(null);
    const backfillPercorsoId = ref('');
    const backfillRunning = ref(false);
    const backfillStatus = ref('');
    const backfillError = ref('');
    const rangeBackfillRunning = ref(false);
    const rangeBackfillStatus = ref('');
    const rangeBackfillError = ref('');
    const rangePreviewRows = ref([]);
    const rangePreviewLimit = ref(0);
    const diagnosticRunning = ref(false);
    const diagnosticStatus = ref('');
    const diagnosticError = ref('');
    const diagnosticResult = ref(null);
    const diagnosticUserKey = ref('');
    const diagnosticUserRunning = ref(false);
    const diagnosticUserStatus = ref('');
    const diagnosticUserError = ref('');
    const diagnosticUserResult = ref(null);
    const noteTimers = new Map();
    const pendingNotes = new Map();

    const dateFormatter = new Intl.DateTimeFormat('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });

    const formatDateOnly = (value) => {
      const date = toDateSafe(value);
      return date ? dateFormatter.format(date) : '';
    };

    const resetToDefaultRange = () => {
      const today = new Date();
      const yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);
      filterFrom.value = toDateKey(yesterday);
      filterTo.value = toDateKey(today);
    };

    const applyInitialRouteFilters = () => {
      const from = getQueryValue(route.query.from);
      const to = getQueryValue(route.query.to);
      if (isValidDateKey(from) && isValidDateKey(to)) {
        filterFrom.value = from;
        filterTo.value = to;
      } else {
        resetToDefaultRange();
      }

      const percorsoId = getQueryValue(route.query.percorsoId).trim();
      if (percorsoId) {
        backfillPercorsoId.value = percorsoId;
      }
    };

    const normalizeStudioName = (value) => {
      if (!value) return '';
      return value
        .toString()
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ');
    };

    const loadReminders = async () => {
      loading.value = true;
      try {
        const fromValue = filterFrom.value;
        const toValue = filterTo.value;
        if (!fromValue || !toValue) {
          rows.value = [];
          return;
        }

        const fromDate = new Date(`${fromValue}T00:00:00`);
        const toDate = new Date(`${toValue}T23:59:59.999`);
        if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) {
          rows.value = [];
          return;
        }

        const remindersQuery = query(
          collection(db, 'ticket_reminders'),
          where('followUpDays', '==', resolvedFollowUpDays.value),
          where('dueAt', '>=', fromDate),
          where('dueAt', '<=', toDate),
          orderBy('dueAt', 'asc')
        );

        const snap = await getDocs(remindersQuery);
        const nowMs = Date.now();
        const reminders = snap.docs.map((docSnap) => {
          const data = docSnap.data() || {};
          const dueAt = toDateSafe(data.dueAt);
          const dueMs = dueAt ? dueAt.getTime() : NaN;
          const daysDiff = Number.isFinite(dueMs) ? buildDaysDiff(dueMs, nowMs) : null;
          return {
            id: docSnap.id,
            key: docSnap.id,
            ...data,
            dueAt,
            dueMs,
            daysDiff,
            controllato: data.controllato ?? false,
            note: data.note ?? '',
            sequenceLabel: data.sequenceTotal
              ? `${data.sequenceIndex || 1}/${data.sequenceTotal}`
              : '',
          };
        });

        const uidList = reminders
          .map((row) => row.uid)
          .filter((uid) => uid && !userCache.has(uid));
        const chunks = chunkList(uidList, USER_CHUNK_SIZE);
        for (const chunk of chunks) {
          const usersSnap = await getDocs(
            query(collection(db, 'users'), where(documentId(), 'in', chunk))
          );
          usersSnap.forEach((docSnap) => {
            userCache.set(docSnap.id, { uid: docSnap.id, ...docSnap.data() });
          });
        }

        rows.value = reminders.map((row) => {
          const user = row.uid ? userCache.get(row.uid) || {} : {};
          const userStudio = user.studio || row.userStudio || row.studio || '';
          const userLabel = [user.name, user.surname].filter(Boolean).join(' ').trim()
            || user.email
            || row.userEmail
            || row.uid
            || '—';
          return {
            ...row,
            userEmail: row.userEmail || user.email || '',
            userLabel,
            userStudio: userStudio || '',
          };
        });
      } catch (error) {
        console.error(`Errore caricamento notifiche ${resolvedFollowUpDays.value} giorni:`, error);
        rows.value = [];
      } finally {
        loading.value = false;
      }
    };

    const filteredRows = computed(() => {
      const list = rows.value || [];
      const filter = statusFilter.value;
      const studio = studioFilter.value;
      return list.filter((row) => {
        let statusOk = true;
        if (filter === 'today') statusOk = row.daysDiff === 0;
        else if (filter === 'overdue') statusOk = row.daysDiff < 0;
        else if (filter === 'upcoming') statusOk = row.daysDiff > 0 && row.status === 'upcoming';
        else if (filter) statusOk = row.status === filter;

        if (!statusOk) return false;

        if (!studio) return true;
        const normalized = normalizeStudioName(row.userStudio || row.studio || '');
        if (studio === NO_STUDIO_FILTER) return !normalized;
        return normalizeStudioName(studio) === normalized;
      });
    });

    const statusLabel = (row) => {
      if (!row) return '';
      if (row.status === 'sent') return 'Inviata';
      if (row.status === 'blocked') {
        if (row.blockedByCheck) return 'Bloccato (check)';
        if (row.blockedByFortyDayRules) return 'Bloccato (gestito)';
        return 'Bloccato';
      }
      if (row.status === 'disabled') return 'Notifiche off';
      if (row.daysDiff === 0) return 'Oggi';
      if (row.daysDiff < 0) return 'In ritardo';
      return 'In arrivo';
    };

    const statusClass = (row) => {
      if (!row) return '';
      if (row.status === 'sent') return 'status-sent';
      if (row.status === 'blocked') return 'status-blocked';
      if (row.status === 'disabled') return 'status-disabled';
      if (row.daysDiff < 0) return 'status-overdue';
      if (row.daysDiff === 0) return 'status-today';
      return 'status-upcoming';
    };

    const fortyDayBlockLabel = (row) => {
      const reasons = Array.isArray(row?.fortyDayBlockReasons) ? row.fortyDayBlockReasons : [];
      if (reasons.includes('later_activity')) return 'Bloccato: attività successiva rilevata';
      if (reasons.includes('current_check_managed')) return 'Bloccato: check già gestito';
      if (reasons.includes('check_missing')) return 'Bloccato: check mancante';
      if (reasons.includes('history_locked')) return 'Bloccato: percorso storico';
      if (reasons.includes('percorso_completed') || reasons.includes('check_completed')) {
        return 'Bloccato: percorso completato';
      }
      return 'Bloccato dalle regole 40 giorni';
    };

    const timingLabel = (row) => {
      if (!row || row.daysDiff == null) return '—';
      if (row.status === 'sent') {
        return row.sentAt ? `Inviata il ${formatDateOnly(row.sentAt)}` : 'Inviata';
      }
      if (row.blockedByCheck) return 'Bloccato (check completato)';
      if (row.blockedByFortyDayRules) return fortyDayBlockLabel(row);
      if (row.status === 'blocked') {
        return row.blockedAt ? `Bloccato il ${formatDateOnly(row.blockedAt)}` : 'Bloccato';
      }
      if (row.status === 'disabled') return 'Invio sospeso (notifiche off)';
      if (row.resumeAt) return 'Programmato per il prossimo ciclo';
      const abs = Math.abs(row.daysDiff);
      if (row.daysDiff > 0) return abs === 1 ? 'Tra 1 giorno' : `Tra ${abs} giorni`;
      if (row.daysDiff === 0) return 'Oggi';
      return abs === 1 ? 'In ritardo di 1 giorno' : `In ritardo di ${abs} giorni`;
    };

    const openInNewTab = (route) => {
      const resolved = router.resolve(route);
      window.open(resolved.href, '_blank', 'noopener');
    };

    const openUser = (uid) => {
      if (!uid) return;
      const route = { name: 'UserProgrammi', params: { userId: uid } };
      const resolved = router.resolve(route);
      const pendingTab = window.open('about:blank', '_blank');
      if (pendingTab) {
        pendingTab.opener = null;
        pendingTab.location.href = resolved.href;
      } else {
        openInNewTab(route);
      }
    };

    const getCandidateUserIds = (row) => {
      if (!row) return [];
      if (typeof row === 'string') return [row];
      return Array.from(new Set([row.uid, row.userId, row.userUid].filter(Boolean)));
    };

    const fetchTicketIdForUser = async (userIds) => {
      const candidateIds = Array.isArray(userIds) ? userIds : [userIds];
      const uniqueIds = Array.from(new Set(candidateIds.filter(Boolean)));
      if (!uniqueIds.length) {
        console.error('Errore: userId non definito');
        return null;
      }

      try {
        const ticketsRef = collection(db, 'assistenzatecnica');
        const q =
          uniqueIds.length === 1
            ? query(ticketsRef, where('userId', '==', uniqueIds[0]))
            : query(ticketsRef, where('userId', 'in', uniqueIds.slice(0, 10)));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          return querySnapshot.docs[0].id;
        }
        return null;
      } catch (error) {
        console.error('Errore nel recupero del ticketId:', error);
        return null;
      }
    };

    const openTechnicalTicket = async (row) => {
      const candidateIds = getCandidateUserIds(row);
      const userId = candidateIds[0];
      if (!userId) {
        console.error('Errore: userId non definito');
        return;
      }

      const pendingTab = window.open('about:blank', '_blank');
      if (pendingTab) {
        pendingTab.opener = null;
      }

      try {
        const ticketId = await fetchTicketIdForUser(candidateIds);

        if (ticketId) {
          const route = {
            name: 'UserConversationTecnica',
            params: { userId, ticketId },
          };
          const resolved = router.resolve(route);
          if (pendingTab) {
            pendingTab.location.href = resolved.href;
          } else {
            openInNewTab(route);
          }
          return;
        }

        if (pendingTab) {
          pendingTab.close();
        }
        alert('Nessuna conversazione tecnica disponibile per questo utente.');
      } catch (error) {
        if (pendingTab) {
          pendingTab.close();
        }
        console.error("Errore durante l'apertura del ticket tecnico:", error);
      }
    };

    const saveControllato = async (row) => {
      if (!row?.id) return;
      try {
        await updateDoc(doc(db, 'ticket_reminders', row.id), {
          controllato: !!row.controllato
        });
      } catch (err) {
        console.error('Errore salvataggio controllato:', err);
      }
    };

    const saveNote = async (row, value) => {
      if (!row?.id) return;
      try {
        await updateDoc(doc(db, 'ticket_reminders', row.id), {
          note: (value || '').toString()
        });
      } catch (err) {
        console.error('Errore salvataggio note:', err);
      }
    };

    const queueNoteSave = (row, immediate = false) => {
      if (!row?.id) return;
      const value = (row.note || '').toString();
      pendingNotes.set(row.id, value);
      if (noteTimers.has(row.id)) {
        clearTimeout(noteTimers.get(row.id));
        noteTimers.delete(row.id);
      }
      if (immediate) {
        saveNote(row, value);
        return;
      }
      const timer = setTimeout(() => {
        noteTimers.delete(row.id);
        const latest = pendingNotes.get(row.id);
        saveNote(row, latest);
      }, 600);
      noteTimers.set(row.id, timer);
    };

    const runBackfill = async () => {
      if (!canRunBackfill.value) return;
      if (backfillRunning.value) return;
      backfillRunning.value = true;
      backfillStatus.value = 'Avvio backfill…';
      backfillError.value = '';
      try {
        const functions = getFunctions();
        const callable = httpsCallable(functions, 'backfillTicketRemindersManual');
        const payload = {};
        payload.followUpDays = resolvedFollowUpDays.value;
        const percorsoId = (backfillPercorsoId.value || '').trim();
        if (percorsoId) {
          payload.percorsoId = percorsoId;
        } else if (Number.isFinite(backfillLimit.value) && backfillLimit.value > 0) {
          payload.maxPercorsi = backfillLimit.value;
        }
        const res = await callable(payload);
        const data = res?.data || {};
        if (data.mode === 'single') {
          backfillStatus.value = `Backfill completato (${data.percorsoId}). Scritti: ${data.written || 0}, rimossi: ${data.removed || 0}.`;
        } else {
          backfillStatus.value = `Backfill completato. Percorsi: ${data.processed || 0}, record: ${data.written || 0}.`;
        }
      } catch (err) {
        console.error('Errore backfill manuale:', err);
        backfillError.value = 'Errore durante il backfill. Controlla la console.';
        backfillStatus.value = '';
      } finally {
        backfillRunning.value = false;
      }
    };

    const runRangeBackfill = async () => {
      if (!canRunBackfill.value) return;
      if (rangeBackfillRunning.value) return;
      if (!filterFrom.value || !filterTo.value) {
        rangeBackfillError.value = 'Seleziona un intervallo date.';
        return;
      }
      rangeBackfillRunning.value = true;
      rangeBackfillStatus.value = 'Analisi e salvataggio in corso…';
      rangeBackfillError.value = '';
      rangePreviewRows.value = [];
      rangePreviewLimit.value = 0;
      try {
        const functions = getFunctions();
        const callable = httpsCallable(functions, 'backfillTicketRemindersByDueDate', {
          timeout: 540000,
        });
        const payload = {
          fromDate: filterFrom.value,
          toDate: filterTo.value,
          commit: true,
          previewLimit: 200,
          followUpDays: resolvedFollowUpDays.value,
        };
        if (Number.isFinite(backfillLimit.value) && backfillLimit.value > 0) {
          payload.maxPercorsi = backfillLimit.value;
        }
        const res = await callable(payload);
        const data = res?.data || {};
        rangePreviewRows.value = Array.isArray(data.preview) ? data.preview : [];
        rangePreviewLimit.value = Number(data.previewLimit) || 0;
        rangeBackfillStatus.value = `Range ${data.fromDate || filterFrom.value} → ${data.toDate || filterTo.value}. ` +
          `Percorsi: ${data.processed || 0}. Trovati: ${data.matched || 0}. Scritti: ${data.written || 0}.`;
        await loadReminders();
      } catch (err) {
        console.error('Errore backfill range:', err);
        rangeBackfillError.value = 'Errore durante analisi/salvataggio. Controlla la console.';
        rangeBackfillStatus.value = '';
      } finally {
        rangeBackfillRunning.value = false;
      }
    };

    const runDiagnostic = async () => {
      if (diagnosticRunning.value) return;
      const percorsoId = (backfillPercorsoId.value || '').trim();
      if (!percorsoId) {
        diagnosticError.value = 'Inserisci un Percorso ID.';
        return;
      }
      diagnosticRunning.value = true;
      diagnosticStatus.value = 'Esecuzione diagnostica…';
      diagnosticError.value = '';
      diagnosticResult.value = null;
      try {
        const functions = getFunctions();
        const callable = httpsCallable(functions, 'diagnoseTicketReminder', {
          timeout: 120000,
        });
        const res = await callable({
          percorsoId,
          followUpDays: resolvedFollowUpDays.value,
        });
        diagnosticResult.value = res?.data || null;
        if (!diagnosticResult.value?.exists) {
          diagnosticStatus.value = 'Percorso non trovato nel progetto della funzione.';
        } else {
          diagnosticStatus.value = 'Diagnostica completata.';
        }
      } catch (err) {
        console.error('Errore diagnostica percorso:', err);
        diagnosticError.value = 'Errore diagnostica. Controlla la console.';
        diagnosticStatus.value = '';
      } finally {
        diagnosticRunning.value = false;
      }
    };

    const runUserDiagnostic = async () => {
      if (diagnosticUserRunning.value) return;
      const key = (diagnosticUserKey.value || '').trim();
      if (!key) {
        diagnosticUserError.value = 'Inserisci email o uid.';
        return;
      }
      diagnosticUserRunning.value = true;
      diagnosticUserStatus.value = 'Esecuzione diagnostica utente…';
      diagnosticUserError.value = '';
      diagnosticUserResult.value = null;
      try {
        const functions = getFunctions();
        const callable = httpsCallable(functions, 'diagnoseTicketRemindersByUser', {
          timeout: 180000,
        });
        const payload = { maxPercorsi: Number(backfillLimit.value) || 50 };
        payload.followUpDays = resolvedFollowUpDays.value;
        if (key.includes('@')) payload.email = key;
        else payload.uid = key;
        const res = await callable(payload);
        diagnosticUserResult.value = res?.data || null;
        diagnosticUserStatus.value = 'Diagnostica utente completata.';
      } catch (err) {
        console.error('Errore diagnostica utente:', err);
        diagnosticUserError.value = 'Errore diagnostica utente. Controlla la console.';
        diagnosticUserStatus.value = '';
      } finally {
        diagnosticUserRunning.value = false;
      }
    };

    const diagnosticRows = computed(() =>
      Array.isArray(diagnosticResult.value?.steps) ? diagnosticResult.value.steps : []
    );
    const diagnosticUserSummary = computed(() =>
      diagnosticUserResult.value || { found: 0, maxPercorsi: 0 }
    );
    const diagnosticUserRows = computed(() => {
      const rows = [];
      const percorsi = diagnosticUserResult.value?.percorsi || [];
      percorsi.forEach((p) => {
        const steps = Array.isArray(p.steps) ? p.steps : [];
        steps.forEach((s) => {
          rows.push({
            ...s,
            percorsoId: p.percorsoId,
            userEmail: p.userEmail,
            uid: p.uid,
          });
        });
      });
      return rows;
    });

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      authUser.value = user || null;
    });

    onMounted(() => {
      applyInitialRouteFilters();
      booted.value = true;
      loadReminders();
    });

    onUnmounted(() => {
      unsubscribeAuth();
    });

    watch([filterFrom, filterTo, resolvedFollowUpDays], () => {
      if (!booted.value) return;
      if (!filterFrom.value || !filterTo.value) return;
      rangePreviewRows.value = [];
      rangePreviewLimit.value = 0;
      loadReminders();
    });

    watch(backfillPercorsoId, () => {
      diagnosticResult.value = null;
      diagnosticStatus.value = '';
      diagnosticError.value = '';
    });
    watch(diagnosticUserKey, () => {
      diagnosticUserResult.value = null;
      diagnosticUserStatus.value = '';
      diagnosticUserError.value = '';
    });

    return {
      loading,
      rows,
      resolvedPageTitle,
      resolvedPageSubtitle,
      filterFrom,
      filterTo,
      statusFilter,
      studioFilter,
      filtersOpen,
      filteredRows,
      formatDateOnly,
      resetToDefaultRange,
      statusLabel,
      statusClass,
      timingLabel,
      openUser,
      openTechnicalTicket,
      saveControllato,
      queueNoteSave,
      studioOptions: STUDIO_OPTIONS,
      noStudioFilterValue: NO_STUDIO_FILTER,
      canUseReminderTools,
      canRunBackfill,
      backfillLimit,
      backfillPercorsoId,
      backfillRunning,
      backfillStatus,
      backfillError,
      rangeBackfillRunning,
      rangeBackfillStatus,
      rangeBackfillError,
      rangePreviewRows,
      rangePreviewLimit,
      runBackfill,
      runRangeBackfill,
      diagnosticRunning,
      diagnosticStatus,
      diagnosticError,
      diagnosticResult,
      diagnosticRows,
      runDiagnostic,
      diagnosticUserKey,
      diagnosticUserRunning,
      diagnosticUserStatus,
      diagnosticUserError,
      diagnosticUserResult,
      diagnosticUserRows,
      diagnosticUserSummary,
      runUserDiagnostic,
    };
  }
};
</script>

<style scoped>
.dataset-summary {
  margin: 8px 0 12px;
  display: flex;
  gap: 12px;
  font-size: 0.9rem;
  color: #475569;
}

.card-surface {
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
  padding: 18px 20px;
  margin-top: 18px;
}

.toolbar-card {
  margin-top: 24px;
}

.filters-header {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.accordion-toggle {
  flex: 1 1 320px;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #0f172a;
  cursor: pointer;
  transition: border 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.accordion-toggle:hover {
  background: #fff;
  border-color: #cbd5e1;
  box-shadow: 0 12px 26px rgba(15, 23, 42, 0.08);
}

.accordion-toggle .material-icons {
  font-size: 22px;
  color: #475569;
}

.toggle-copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.toggle-title {
  font-weight: 700;
  font-size: 0.95rem;
}

.toggle-subtitle {
  font-size: 0.82rem;
  color: #64748b;
}

.accordion-panel {
  margin-top: 14px;
  padding: 14px 12px;
  border-radius: 14px;
  border: 1px solid #f1f5f9;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6);
}

.accordion-enter-active,
.accordion-leave-active {
  transition: all 0.18s ease;
}

.accordion-enter,
.accordion-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
}

.toolbar-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 0 0 auto;
}

.toolbar-group--lg { width: 240px; }
.toolbar-group--md { width: 200px; }
.toolbar-group--sm { width: 160px; }

.toolbar-actions {
  display: flex;
  gap: 12px;
  flex: 0 0 auto;
  align-items: center;
}

.toolbar-label {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
}

.date-range-input {
  width: 100%;
}

.date-range-input--short {
  width: 82% !important;
}

.input {
  width: 100%;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #0f172a;
  transition: border 0.2s ease, box-shadow 0.2s ease;
}

.input:focus {
  border-color: #2563eb;
  background: #fff;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
  outline: none;
}

.input--compact {
  max-width: 220px;
  padding: 8px 12px;
}

.note-input {
  min-width: 220px;
}

.checkbox-inline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.checkbox-inline input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: #2563eb;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border-radius: 12px;
  border: 1px solid transparent;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
  background: #f1f5f9;
  color: #1e293b;
  border-color: #e2e8f0;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.12);
}

.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}

.btn.primary {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: #fff;
}

.btn.ghost {
  background: #f1f5f9;
  color: #1e293b;
  border-color: #e2e8f0;
}

.backfill-feedback {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  font-size: 0.85rem;
}

.buttons-container {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.buttons-container button {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.3);
  background: #f7f9fc;
  color: #475569;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease, color 0.2s ease;
}

.buttons-container button:hover {
  background: #e0e7ff;
  border-color: rgba(99, 102, 241, 0.6);
  color: #4338ca;
  transform: translateY(-1px);
}

.buttons-container .material-icons {
  font-size: 20px;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.status-upcoming {
  background: #e0f2fe;
  color: #0369a1;
}

.status-today {
  background: #fef3c7;
  color: #92400e;
}

.status-overdue {
  background: #fee2e2;
  color: #b91c1c;
}

.status-sent {
  background: #e5e7eb;
  color: #374151;
}

.status-blocked {
  background: #ede9fe;
  color: #6b21a8;
}

.status-disabled {
  background: #f1f5f9;
  color: #64748b;
}

.empty-state {
  margin-top: 16px;
}
</style>
